import {
  Badge,
  Button,
  Card,
  Loader,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Download, Search } from "lucide-react";

import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api/client";
import { downloadClinicForm } from "@/lib/api/admin";
import { formatDateTime } from "@/lib/format";

interface AdminFormRow {
  id: string;
  clinic_id: string;
  clinic_name: string;
  examination_id: string;
  template: string;
  form_number: string;
  generated_at: string;
  revoked_at: string | null;
  patient_name: string;
  patient_card: string;
}

export function AdminFormsPage() {
  const [search, setSearch] = useState("");
  const [debSearch] = useDebouncedValue(search, 250);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-all-forms"],
    queryFn: async () => {
      const { data } = await api.get<AdminFormRow[]>("/admin/forms");
      return data;
    },
  });

  const filtered = (data ?? []).filter((f) => {
    if (!debSearch.trim()) return true;
    const q = debSearch.toLowerCase();
    return (
      f.form_number.toLowerCase().includes(q) ||
      f.patient_name.toLowerCase().includes(q) ||
      f.patient_card.toLowerCase().includes(q) ||
      f.clinic_name.toLowerCase().includes(q) ||
      f.template.toLowerCase().includes(q)
    );
  });

  const downloadOne = async (clinicId: string, formId: string, formNumber: string) => {
    try {
      const blob = await downloadClinicForm(clinicId, formId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${formNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      notifications.show({ color: "red", message: "PDF yuklab bo'lmadi" });
    }
  };

  return (
    <Stack gap="lg">
      <PageHeader title="Barcha hujjatlar" subtitle={`${filtered.length} ta forma`} />

      <Card padding="md">
        <TextInput
          rightSection={<Search size={18} strokeWidth={2} />}
          placeholder="Hujjat raqami, bemor, klinika..."
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
      </Card>

      <Card padding={0} radius="xl">
        <Table.ScrollContainer minWidth={1000}>
          <Table verticalSpacing="md" striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Klinika</Table.Th>
                <Table.Th>Hujjat raqami</Table.Th>
                <Table.Th>Bemor</Table.Th>
                <Table.Th>Forma turi</Table.Th>
                <Table.Th>Holat</Table.Th>
                <Table.Th>Yaratilgan</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading && (
                <Table.Tr><Table.Td colSpan={7}><Loader size="sm" /></Table.Td></Table.Tr>
              )}
              {filtered.map((f) => (
                <Table.Tr key={f.id}>
                  <Table.Td>
                    <Badge variant="light" color="brand" radius="sm">{f.clinic_name}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={700} ff="monospace" c="brand.7">{f.form_number}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={0}>
                      <Text size="sm" fw={600}>{f.patient_name}</Text>
                      <Text size="xs" c="dimmed" ff="monospace">{f.patient_card}</Text>
                    </Stack>
                  </Table.Td>
                  <Table.Td><Badge variant="light" radius="sm">{f.template}</Badge></Table.Td>
                  <Table.Td>
                    <Badge
                      variant="light"
                      color={f.revoked_at ? "red" : "green"}
                      radius="sm"
                    >
                      {f.revoked_at ? "Bekor qilingan" : "Faol"}
                    </Badge>
                  </Table.Td>
                  <Table.Td><Text size="xs" c="dimmed">{formatDateTime(f.generated_at)}</Text></Table.Td>
                  <Table.Td>
                    <Button
                      size="xs"
                      variant="light"
                      radius="xl"
                      leftSection={<Download size={14} strokeWidth={2.2} />}
                      onClick={() => downloadOne(f.clinic_id, f.id, f.form_number)}
                    >
                      Yuklab olish
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>
    </Stack>
  );
}
